import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { AlertTriangle, Building2, Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button, Card, errorMessage } from "@/components/ui";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Accepting an invitation.
 *
 * Every invitation email the product has ever sent linked here, and until now
 * this route did not exist — so nobody could join, from any path. That is the
 * bug this page closes; the platform-invitation flow is what it also enables.
 *
 * One page for both kinds, because the person clicking a link should not have
 * to know which they got. The API says what the token is for, and the only
 * visible difference is one extra field: an invitation to the platform asks you
 * to name your workspace, an invitation to a workspace tells you which one you
 * are joining.
 */
export function AcceptInvitation() {
  const { t } = useTranslation("auth");
  const { token } = useParams({ from: "/accept-invitation/$token" });

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");

  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.getInvitation(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      api.acceptInvitation(token, {
        name: name.trim(),
        password,
        workspaceName: workspaceName.trim() || undefined,
      }),
    onSuccess: () => {
      // A document navigation rather than a router push. The session cookie was
      // set by this response, and Better Auth's client hooks are module-level
      // atoms that do not refetch — a soft transition renders the signed-out
      // shell against stale state. See CLAUDE.md.
      window.location.assign("/");
    },
  });

  if (invitation.isLoading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-text-muted">
          <Loader2 size={14} className="animate-spin" />
          {t("accept.checking")}
        </div>
      </Shell>
    );
  }

  if (invitation.error || !invitation.data) {
    return (
      <Shell>
        <Problem
          title={t("accept.invalidTitle")}
          body={t("accept.invalidBody")}
        />
      </Shell>
    );
  }

  const data = invitation.data;

  if (data.used) {
    return (
      <Shell>
        <Problem
          title={t("accept.usedTitle")}
          body={t("accept.usedBody")}
          action={{ label: t("accept.goToSignIn"), href: "/" }}
        />
      </Shell>
    );
  }

  if (data.expired) {
    return (
      <Shell>
        <Problem
          title={t("accept.expiredTitle")}
          body={t("accept.expiredBody")}
        />
      </Shell>
    );
  }

  const isPlatform = data.kind === "platform";
  const canSubmit =
    name.trim().length > 0 &&
    password.length >= 12 &&
    (!isPlatform || workspaceName.trim().length > 0);

  return (
    <Shell>
      <div className="mb-5">
        <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-accent-subtle text-accent">
          {isPlatform ? <Sparkles size={17} /> : <Building2 size={17} />}
        </div>
        <h1 className="text-[19px] font-semibold tracking-tight text-text">
          {isPlatform
            ? t("accept.platformTitle")
            : t("accept.joinTitle", { name: data.organizationName ?? "" })}
        </h1>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">
          {isPlatform
            ? t("accept.platformIntro")
            : t("accept.joinIntro", { name: data.organizationName ?? "" })}
        </p>
        {data.note ? (
          <p className="mt-2.5 rounded-lg bg-bg-subtle px-3 py-2 text-[13px] leading-relaxed text-text-muted">
            {data.note}
          </p>
        ) : null}
      </div>

      <form
        className="space-y-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) accept.mutate();
        }}
      >
        <Field label={t("accept.email")}>
          {/* Fixed: the invitation is for this address, and letting it be edited
              would silently create an account the invitation does not cover. */}
          <input
            value={data.email}
            readOnly
            className="h-10 w-full cursor-not-allowed rounded-lg border border-border bg-bg-subtle px-3 text-sm text-text-muted"
          />
        </Field>

        <Field label={t("accept.name")}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("accept.namePlaceholder")}
            autoFocus
            className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
        </Field>

        {isPlatform ? (
          <Field label={t("accept.workspaceName")}>
            <input
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder={t("accept.workspacePlaceholder")}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
            <span className="mt-1.5 block text-[12px] text-text-faint">
              {t("accept.workspaceHint")}
            </span>
          </Field>
        ) : null}

        <Field label={t("accept.password")}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("accept.passwordPlaceholder")}
            autoComplete="new-password"
            className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
          {password.length > 0 && password.length < 12 ? (
            <span className="mt-1.5 block text-[12px] text-warning">
              {t("password.moreNeeded", { count: 12 - password.length })}
            </span>
          ) : null}
        </Field>

        {accept.error ? (
          <p
            role="alert"
            className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] leading-relaxed text-danger"
          >
            {errorMessage(accept.error, t("accept.genericError"))}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          disabled={!canSubmit || accept.isPending}
          className="w-full"
        >
          {accept.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
          {isPlatform ? t("accept.createWorkspace") : t("accept.join", { name: data.organizationName ?? "" })}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-5 py-10">
      <Card className="w-full max-w-[440px] p-7">{children}</Card>
      <div className="mt-5">
        <LanguageSwitcher variant="select" />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-text">{label}</span>
      {children}
    </label>
  );
}

function Problem({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; href: string };
}) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-danger-subtle text-danger">
        <AlertTriangle size={17} />
      </div>
      <h1 className="text-[16px] font-semibold text-text">{title}</h1>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-text-muted">{body}</p>
      {action ? (
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => window.location.assign(action.href)}
        >
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
